import {
  Outlet,
  createRootRoute,
  HeadContent,
  retainSearchParams,
  useNavigate,
  useLocation,
} from '@tanstack/react-router';
import 'ui_2/src/index.css';
import { useDappState } from '@src/hooks/useDappState';
import { useEffect } from 'react';
import { z } from 'zod';
import { connect, getIsConnected, useOnDisconnect } from '@puzzlehq/sdk';
import { useAuth } from '@src/data/settings';
import { ErrorMessage } from '@src/components/ErrorMessage';

const addressSchema = z.object({
  address: z.string().optional(),
  auth: z.string().optional(),
  challengeAuth: z.string().optional(),
});

export const Route = createRootRoute({
  validateSearch: addressSchema,
  search: {
    middlewares: [retainSearchParams(['address', 'auth', 'challengeAuth'])],
  },
  head: () => ({
    meta: [
      {
        name: 'description',
        content:
          'Earn Puzzle Arcade Coins ($PARC) that enable you to play Puzzle Arcade Games.',
      },
      {
        title: 'Puzzle Arcade Quests',
      },
    ],
  }),
  errorComponent: function Component({ error }) {
    return <ErrorMessage error={error} />;
  },
  beforeLoad: async () => {
    const isPuzzleWalletWebview =
      window.navigator.userAgent?.includes('PuzzleWallet');
    if (isPuzzleWalletWebview === false) return;

    try {
      const isConnected = await getIsConnected();
      if (isConnected) return;
      const connectResponse = await connect({
        permissions: {
          programIds: {
            [import.meta.env.VITE_NETWORK!]: [
              import.meta.env.VITE_COIN_CONTRACT!,
              import.meta.env.VITE_TICKET_CONTRACT!,
            ],
          },
        },
        dAppInfo: {
          name: 'Puzzle Arcade Quests',
        },
      });
      console.log(`/ connectResponse ${JSON.stringify(connectResponse)}`);
    } catch (e) {
      console.error(`Error calling connect on app startup`, e);
    }
  },
  component: function Component() {
    const navigate = useNavigate({ from: Route.fullPath });
    const { account } = useDappState();

    const { pathname } = useLocation();

    useEffect(() => {
      if (account?.address) {
        navigate({
          to: pathname,
          search: (prev) => ({ ...prev, address: account.address }),
        });
      }
    }, [account?.address, navigate]);

    useOnDisconnect(() => {
      navigate({
        to: pathname,
        search: (prev) => ({ ...prev, address: undefined }),
      });
    }, []);

    const { address: searchAddress, auth, challengeAuth } = Route.useSearch();
    const [_auth, setAuth, _challengeAuth, setChallengeAuth, setAddress] =
      useAuth((state) => [
        state.auth,
        state.setAuth,
        state.challengeAuth,
        state.setChallengeAuth,
        state.setAddress,
      ]);

    useEffect(() => {
      const addr = account?.address ?? searchAddress;
      if (addr) {
        setAddress(addr);
      }
    }, [account?.address, searchAddress]);

    useEffect(() => {
      console.log(`auth: ${auth}`);
      console.log(`challengeAuth: ${challengeAuth}`);
      if (challengeAuth && challengeAuth !== _challengeAuth) {
        setChallengeAuth(challengeAuth);
        navigate({
          to: pathname,
          search: (prev) => ({ ...prev, challengeAuth: undefined }),
        });
      } else if (auth && auth !== _auth) {
        setAuth(auth);
        navigate({
          to: pathname,
          search: (prev) => ({ ...prev, auth: undefined }),
        });
      }
    }, [auth, challengeAuth]);

    useEffect(() => {
      console.log(`_auth: ${_auth}`);
      console.log(`_challengeAuth: ${_challengeAuth}`);
    }, [_auth, _challengeAuth]);

    return (
      <>
        <HeadContent />
        <div className='h-[100dvh] w-full'>
          <Outlet />
        </div>
      </>
    );
  },
});
